import React, { useState, useEffect } from 'react';
import './booking.css';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCalendar, FiMapPin, FiUser, FiPhone, FiMail, FiClock, FiUsers, FiCheck } from 'react-icons/fi';
import { FaCar, FaShieldAlt, FaStar } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { db } from '../FirebaseComp/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import emailjs from 'emailjs-com';

function Booking() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    vehicleType: 'sedan',
    pickup: '',
    destination: '',
    date: '',
    time: '',
    passengers: 1,
    specialRequests: ''
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // EmailJS Configuration
  const EMAILJS_SERVICE_ID = 'service_fmzoeen';
  const EMAILJS_TEMPLATE_ID = 'template_6hnsn55';
  const EMAILJS_PUBLIC_KEY = 'd5Fex9XpUljSzKPby';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData.name.trim()) newErrors.name = 'Name is required';
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = 'Email is invalid';
      }
      if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
      if (!formData.date) newErrors.date = 'Date is required';
      if (!formData.time) newErrors.time = 'Time is required';
    }
    
    if (step === 2) {
      if (!formData.pickup.trim()) newErrors.pickup = 'Pickup location is required';
      if (!formData.destination.trim()) newErrors.destination = 'Destination is required';
      if (!formData.passengers || formData.passengers < 1) newErrors.passengers = 'Number of passengers is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
    window.scrollTo(0, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const isStep1Valid = validateStep(1);
    const isStep2Valid = validateStep(2);
    
    if (!isStep1Valid || !isStep2Valid) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const docRef = await addDoc(collection(db, 'ceylonwayfinder_bookings'), {
        ...formData,
        passengers: parseInt(formData.passengers),
        createdAt: serverTimestamp(),
        status: 'pending',
        source: 'website'
      });
      
      const emailParams = {
        to_name: formData.name,
        to_email: formData.email,
        booking_id: docRef.id,
        pickup_location: formData.pickup,
        destination: formData.destination,
        date: formData.date,
        time: formData.time,
        vehicle_type: formData.vehicleType,
        passengers: formData.passengers,
        special_requests: formData.specialRequests || 'None',
        phone: formData.phone
      };

      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        emailParams,
        EMAILJS_PUBLIC_KEY
      );
      
      setBookingSuccess(true);
      
    } catch (error) {
      console.error('Error:', error);
      setErrors({ submit: 'Failed to submit booking. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSuccess = () => {
    setBookingSuccess(false);
    navigate('/');
  };

  const vehicleTypes = [
    { id: 'sedan', name: 'Sedan', icon: <FaCar />, capacity: 4, price: 'From LKR 80/km' },
    { id: 'suv', name: 'SUV', icon: <FaCar />, capacity: 6, price: 'From LKR 100/km' },
    { id: 'van', name: 'Van', icon: <FaCar />, capacity: 8, price: 'From LKR 120/km' },
    { id: 'luxury', name: 'Luxury', icon: <FaCar />, capacity: 4, price: 'From LKR 150/km' }
  ];

  const steps = [
    { number: 1, title: 'Your Details', icon: <FiUser /> },
    { number: 2, title: 'Trip Details', icon: <FiMapPin /> },
    { number: 3, title: 'Confirmation', icon: <FiCheck /> }
  ];

  // Success Modal
  if (bookingSuccess) {
    return (
      <AnimatePresence>
        <motion.div 
          className="success-container"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setBookingSuccess(false)}
        >
          <motion.div
            className="success-card"
            initial={{ scale: 0.8, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300 }}
          >
            <button 
              className="close-button" 
              onClick={handleCloseSuccess}
              aria-label="Close"
            >
              &times;
            </button>
            <div className="success-icon">✓</div>
            <h2>Booking Confirmed!</h2>
            <p>Thank you for choosing Ceylon Wayfinder. A confirmation has been sent to your email.</p>
            <button 
              className="confirm-button"
              onClick={handleCloseSuccess}
            >
              Got It!
            </button>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  return (
    <div className="booking-page">
      {/* Hero Section */}

      <section 
        className="booking-hero"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(5, 85, 110, 0.92), rgba(105, 146, 105, 0.85))`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >


        <div className="booking-hero-content">
          <h1 className="booking-hero-title">
            Book Your <span>Premium Ride</span>
          </h1>
          <p className="booking-hero-subtitle">
            Experience reliable, comfortable, and trustworthy taxi services across Sri Lanka
          </p>
          <div className="hero-features">
            <span><FaShieldAlt /> Licensed & Insured</span>
            <span><FaStar /> 5/5 Rating</span>
            <span><FiClock /> 24/7 Support</span>
          </div>
        </div>

      </section>

      <div className="booking-main">
        <motion.div 
          className="booking-container"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Professional Progress Bar */}
          <div className="progress-section">
            <div className="steps-container">
              {steps.map((step, index) => (
                <div 
                  key={step.number}
                  className={`step-wrapper ${currentStep > step.number ? 'completed' : ''} ${currentStep === step.number ? 'active' : ''}`}
                >
                  <div className="step-connector">
                    {index < steps.length - 1 && (
                      <div className={`connector-line ${currentStep > step.number ? 'filled' : ''}`}></div>
                    )}
                  </div>
                  <div className="step-content-wrapper">
                    <div className="step-circle">
                      {currentStep > step.number ? (
                        <FiCheck className="step-icon check" />
                      ) : (
                        <span className="step-number-text">{step.number}</span>
                      )}
                    </div>
                    <div className="step-info">
                      <span className="step-label">STEP {step.number}</span>
                      <span className="step-title-text">{step.title}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit} noValidate>
            {/* Step 1: Personal Details */}
            {currentStep === 1 && (
              <motion.div
                className="form-step"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <h2 className="step-title">Tell us about yourself</h2>
                
                <div className="input-group">
                  <FiUser className="input-icon" />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Full Name"
                    className={errors.name ? 'error' : ''}
                  />
                  {errors.name && <span className="error-message">{errors.name}</span>}
                </div>

                <div className="input-group">
                  <FiMail className="input-icon" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Email Address"
                    className={errors.email ? 'error' : ''}
                  />
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>

                <div className="input-group">
                  <FiPhone className="input-icon" />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Phone Number (e.g., +94771234567)"
                    className={errors.phone ? 'error' : ''}
                  />
                  {errors.phone && <span className="error-message">{errors.phone}</span>}
                </div>

                <div className="input-row">
                  <div className="input-group">
                    <FiCalendar className="input-icon" />
                    <input
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleChange}
                      min={new Date().toISOString().split('T')[0]}
                      className={errors.date ? 'error' : ''}
                    />
                    {errors.date && <span className="error-message">{errors.date}</span>}
                  </div>
                  <div className="input-group">
                    <FiClock className="input-icon" />
                    <input
                      type="time"
                      name="time"
                      value={formData.time}
                      onChange={handleChange}
                      className={errors.time ? 'error' : ''}
                    />
                    {errors.time && <span className="error-message">{errors.time}</span>}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 2: Trip Details */}
            {currentStep === 2 && (
              <motion.div
                className="form-step"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <h2 className="step-title">Where are you going?</h2>

                <div className="input-group">
                  <FiMapPin className="input-icon" />
                  <input
                    type="text"
                    name="pickup"
                    value={formData.pickup}
                    onChange={handleChange}
                    placeholder="Pickup Location"
                    className={errors.pickup ? 'error' : ''}
                  />
                  {errors.pickup && <span className="error-message">{errors.pickup}</span>}
                </div>

                <div className="input-group">
                  <FiMapPin className="input-icon" />
                  <input
                    type="text"
                    name="destination"
                    value={formData.destination}
                    onChange={handleChange}
                    placeholder="Destination"
                    className={errors.destination ? 'error' : ''}
                  />
                  {errors.destination && <span className="error-message">{errors.destination}</span>}
                </div>

                <div className="vehicle-selection">
                  <h3>Select Your Vehicle</h3>
                  <div className="vehicle-options">
                    {vehicleTypes.map(vehicle => (
                      <motion.div
                        key={vehicle.id}
                        className={`vehicle-option ${formData.vehicleType === vehicle.id ? 'selected' : ''}`}
                        onClick={() => setFormData(prev => ({ ...prev, vehicleType: vehicle.id }))}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <div className="vehicle-icon">{vehicle.icon}</div>
                        <div className="vehicle-info">
                          <h4>{vehicle.name}</h4>
                          <p>Up to {vehicle.capacity} passengers</p>
                          <small>{vehicle.price}</small>
                        </div>
                        {formData.vehicleType === vehicle.id && (
                          <div className="selected-check">✓</div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                </div>

                <div className="input-group">
                  <FiUsers className="input-icon" />
                  <input
                    type="number"
                    name="passengers"
                    value={formData.passengers}
                    onChange={handleChange}
                    min="1"
                    max="8"
                    placeholder="Number of Passengers"
                    className={errors.passengers ? 'error' : ''}
                  />
                  {errors.passengers && <span className="error-message">{errors.passengers}</span>}
                </div>
              </motion.div>
            )}

            {/* Step 3: Confirmation */}
            {currentStep === 3 && (
              <motion.div
                className="form-step"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <h2 className="step-title">Confirm Your Booking</h2>
                
                <div className="confirmation-card">
                  <div className="confirmation-section">
                    <h3>Personal Details</h3>
                    <div className="detail-row">
                      <span>Name:</span>
                      <span>{formData.name}</span>
                    </div>
                    <div className="detail-row">
                      <span>Email:</span>
                      <span>{formData.email}</span>
                    </div>
                    <div className="detail-row">
                      <span>Phone:</span>
                      <span>{formData.phone}</span>
                    </div>
                  </div>

                  <div className="confirmation-section">
                    <h3>Trip Details</h3>
                    <div className="detail-row">
                      <span>Pickup:</span>
                      <span>{formData.pickup}</span>
                    </div>
                    <div className="detail-row">
                      <span>Destination:</span>
                      <span>{formData.destination}</span>
                    </div>
                    <div className="detail-row">
                      <span>Date & Time:</span>
                      <span>{formData.date} at {formData.time}</span>
                    </div>
                    <div className="detail-row">
                      <span>Vehicle:</span>
                      <span>{vehicleTypes.find(v => v.id === formData.vehicleType)?.name}</span>
                    </div>
                    <div className="detail-row">
                      <span>Passengers:</span>
                      <span>{formData.passengers}</span>
                    </div>
                  </div>

                  <div className="input-group">
                    <textarea
                      name="specialRequests"
                      value={formData.specialRequests}
                      onChange={handleChange}
                      placeholder="Special Requests (Optional)"
                      rows="3"
                    />
                  </div>
                </div>

                {errors.submit && <div className="error-message submit-error">{errors.submit}</div>}

                <motion.button
                  type="submit"
                  className="submit-button"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="loading-spinner"></span>
                      Processing...
                    </>
                  ) : (
                    'Confirm Booking'
                  )}
                </motion.button>
              </motion.div>
            )}

            {/* Navigation Buttons */}
            <div className="form-navigation">
              {currentStep > 1 && (
                <motion.button
                  type="button"
                  className="nav-button prev"
                  onClick={prevStep}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  ← Back
                </motion.button>
              )}

              {currentStep < 3 && (
                <motion.button
                  type="button"
                  className="nav-button next"
                  onClick={nextStep}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Continue →
                </motion.button>
              )}
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
}

export default Booking;